import { z } from 'zod';
import { environmentalBasisSchema } from './environmental-dashboard.js';
import { sustainabilityCoverageSchema } from './sustainability-trajectory.js';

/**
 * SIL-476 — Fleet detail tab (customer Environmental dashboard), post-demo
 * live slice. Wire DTOs for `GET /api/client/environmental/fleet-detail` —
 * see apps/api/src/routes/client/environmental.ts (route) and apps/customer's
 * `hooks/useEnvironmentalFleetDetail.ts` (consumer).
 *
 * Per `Environmental Fleet Detail.dc.html` as amended by the SIL-459
 * register (resources/plans/sustainability-dashboard-spec.md §3 "Fleet
 * detail"): per-asset-class stacked bars, monthly emissions + MWh overlay,
 * per-site dumbbell, second-life ledger, top emitting assets. The mock's
 * top summary strip / FY30 card / reduction-progress donuts are NOT
 * reproduced here — they reuse the Overview tab's existing `KpiStrip` and
 * `FyGoalTrackingCard` (SIL-474 target model) unchanged, sharing the same
 * `/environmental/overview` query so the two tabs never disagree about the
 * org's totals. The second-life ledger likewise reuses the Overview tab's
 * `avoided` events (same `useAvoidedEventsTape` hook) rather than
 * duplicating that query here — it IS the same per-transaction data,
 * presented as a ledger instead of a tape.
 *
 * Per resources/plans/sil-461-dashboard-data-readiness.md's gap list:
 *   - Gap #4 (no MWh serving surface): `monthlyMwh` below is the new
 *     aggregation this ticket adds — `avg_power_draw_watts *
 *     total_uptime_seconds` summed over `telemetry_daily_agg`, grouped by
 *     calendar month. Tagged 'measured' (a direct telemetry read, no
 *     defaulted inputs) per the readiness doc's own verdict table.
 *   - Gap #6 (`data_center.operated`): migration 719
 *     (`719_customer_org_sustainability_config.sql`) already CANCELLED that
 *     column in favor of an org-level `operated` flag — the per-site
 *     dumbbell below never needed it in the first place (it compares Scope
 *     2 vs Scope 3 magnitude per site, not a Scope2/Cat8 split). The real
 *     gap is `asset.data_center_id` COVERAGE (13.8% fleet-wide, 21.4% Meta)
 *     — disclosed via `siteBreakdown.coverage`, never gated on.
 */

// ---------------------------------------------------------------------------
// Per-asset-class stacked bars (Scope 2 + Scope 3, per class).
// ---------------------------------------------------------------------------

/** REAL-derived — same views the Overview tab's KPI/treemap aggregate, this
 * time split by class AND by scope so the bar can stack Scope 2 under
 * Scope 3 per class (the mock's "Footprint by asset class" module). */
export const environmentalClassBreakdownItemSchema = z.object({
  assetClassId: z.string(),
  assetClassName: z.string(),
  scope2Kgco2e: z.number(),
  scope3Kgco2e: z.number(),
  assetCount: z.number().int().nonnegative(),
});
export type EnvironmentalClassBreakdownItem = z.infer<typeof environmentalClassBreakdownItemSchema>;

export const environmentalClassBreakdownSchema = z.object({
  items: z.array(environmentalClassBreakdownItemSchema),
  basis: environmentalBasisSchema,
});
export type EnvironmentalClassBreakdown = z.infer<typeof environmentalClassBreakdownSchema>;

// ---------------------------------------------------------------------------
// Monthly MWh overlay (gap #4).
// ---------------------------------------------------------------------------

export const environmentalMwhPointSchema = z.object({
  periodKey: z.string(),
  mwh: z.number(),
});
export type EnvironmentalMwhPoint = z.infer<typeof environmentalMwhPointSchema>;

/** `coverage` reuses the same shape/fields the Overview tab's monthly-series
 * data-coverage strip already renders (`DataCoverageStrip`) — asset
 * coverage + months available over `telemetry_daily_agg`, a DIFFERENT
 * surface (and therefore a different coverage figure) than the Overview
 * tab's use-phase coverage strip. */
export const environmentalMonthlyMwhSchema = z.object({
  points: z.array(environmentalMwhPointSchema),
  basis: environmentalBasisSchema,
  coverage: sustainabilityCoverageSchema,
});
export type EnvironmentalMonthlyMwh = z.infer<typeof environmentalMonthlyMwhSchema>;

// ---------------------------------------------------------------------------
// Per-site dumbbell (Scope 2 vs Scope 3 by data_center).
// ---------------------------------------------------------------------------

export const environmentalSiteBreakdownItemSchema = z.object({
  dataCenterId: z.string(),
  dataCenterName: z.string(),
  scope2Kgco2e: z.number(),
  scope3Kgco2e: z.number(),
});
export type EnvironmentalSiteBreakdownItem = z.infer<typeof environmentalSiteBreakdownItemSchema>;

/** Lean coverage shape (no months/period fields — site linkage isn't a
 * time series) for how much of the fleet actually has `data_center_id` set.
 * Caveat-only, per the same "ships caveated, never gated" convention as
 * `sustainabilityCoverageSchema` — a thin-linkage org still sees its real
 * per-site split next to an honest coverage figure, never a blocked
 * widget. */
export const environmentalAssetLinkCoverageSchema = z.object({
  linkedAssetCount: z.number().int().nonnegative(),
  fleetAssetCount: z.number().int().nonnegative(),
  coveragePct: z.number().min(0).max(100),
});
export type EnvironmentalAssetLinkCoverage = z.infer<typeof environmentalAssetLinkCoverageSchema>;

export const environmentalSiteBreakdownSchema = z.object({
  items: z.array(environmentalSiteBreakdownItemSchema),
  basis: environmentalBasisSchema,
  coverage: environmentalAssetLinkCoverageSchema,
});
export type EnvironmentalSiteBreakdown = z.infer<typeof environmentalSiteBreakdownSchema>;

// ---------------------------------------------------------------------------
// Top emitting assets.
// ---------------------------------------------------------------------------

export const environmentalTopEmitterSchema = z.object({
  assetId: z.string(),
  displayName: z.string(),
  assetClassId: z.string(),
  assetClassName: z.string(),
  scope2Kgco2e: z.number(),
  scope3Kgco2e: z.number(),
  totalKgco2e: z.number(),
});
export type EnvironmentalTopEmitter = z.infer<typeof environmentalTopEmitterSchema>;

export const environmentalFleetDetailResponseSchema = z.object({
  classBreakdown: environmentalClassBreakdownSchema,
  monthlyMwh: environmentalMonthlyMwhSchema,
  siteBreakdown: environmentalSiteBreakdownSchema,
  /** Top N (route picks 5) assets by total (Scope 2 annualized + Scope 3
   * embodied) kgCO2e, descending. 'derived' basis — same aggregates the KPI
   * strip already sums, just per-asset and ranked. */
  topEmitters: z.object({
    items: z.array(environmentalTopEmitterSchema),
    basis: environmentalBasisSchema,
  }),
});
export type EnvironmentalFleetDetailResponse = z.infer<typeof environmentalFleetDetailResponseSchema>;
